import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Search, Plus, Pencil, Trash2, X, Save, Package, PackageX, AlertTriangle, CheckCircle2,
  Boxes, Settings, Loader2, History, ArrowUpRight, ArrowDownRight, Minus, Tag, Layers,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtT = (d) => (d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";
const Skeleton = ({ className = '' }) => <div className={`animate-pulse bg-white/[0.05] rounded-lg ${className}`} />;

const DEFAULT_THRESHOLD = 15;
const shortId = (id) => (id || '').slice(0, 8).toUpperCase();
const effThreshold = (p) => {
  const t = p?.low_stock_threshold;
  return (typeof t === 'number' && t >= 0) ? t : DEFAULT_THRESHOLD;
};
const health = (p) => {
  const q = p?.stock_quantity ?? 0;
  if (q <= 0) return { key: 'out', label: 'Out of Stock', color: '#EF4444' };
  if (q <= effThreshold(p)) return { key: 'low', label: 'Low Stock', color: '#F59E0B' };
  return { key: 'in', label: 'In Stock', color: '#10B981' };
};

const ADJUST_REASONS = [
  'Inventory Received', 'Inventory Correction', 'Damaged Product',
  'Lost/Damaged Inventory', 'Physical Count Adjustment', 'Return to Stock', 'Other',
];

const ACTIVITY_LABELS = {
  manual_add: 'Stock Added', manual_remove: 'Stock Removed', manual_set: 'Stock Set',
  order_commit: 'Order Committed', order_cancel_restore: 'Order Cancelled — Restored',
  order_delete_restore: 'Order Deleted — Restored', order_edit_adjustment: 'Order Edited',
  order_return_to_stock: 'Returned to Stock',
};

const HealthBadge = ({ p }) => {
  const m = health(p);
  const Icon = m.key === 'out' ? PackageX : m.key === 'low' ? AlertTriangle : CheckCircle2;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
      style={{ background: `${m.color}1f`, color: m.color }} data-testid="stock-health-badge">
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
};

const Modal = ({ open, onClose, children, max = 'max-w-lg', testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full ${max} bg-[#14171B] border border-white/[0.08] rounded-2xl max-h-[90vh] overflow-y-auto`}>
        {children}
      </div>
    </div>
  );
};

const emptyProduct = { name: '', description: '', category: 'ppf', size: '', finish: '', price: '', stock_quantity: '', sku: '', low_stock_threshold: '15' };

export const ProductsInventory = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyProduct);
  const [editForm, setEditForm] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [showCats, setShowCats] = useState(false);
  const [showActivity, setShowActivity] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    try {
      const pre = sessionStorage.getItem('tc_products_filter');
      if (pre) { setStockFilter(pre); sessionStorage.removeItem('tc_products_filter'); }
    } catch (e) { /* ignore */ }
  }, []);

  const fetchProducts = async () => {
    try {
      const r = await axios.get(`${API}/api/products?limit=500`);
      setProducts(r.data.products || r.data || []);
    } catch (e) { toast.error('Error loading products'); }
    finally { setLoading(false); }
  };
  const fetchCategories = async () => {
    try { const r = await axios.get(`${API}/api/categories`); setCategories(r.data || []); }
    catch (e) { /* ignore */ }
  };

  const catName = (name) => categories.find((c) => c.name === name)?.display_name || name;

  const summary = useMemo(() => {
    const out = products.filter((p) => (p.stock_quantity ?? 0) <= 0).length;
    const low = products.filter((p) => { const q = p.stock_quantity ?? 0; return q > 0 && q <= effThreshold(p); }).length;
    return { total: products.length, low, out };
  }, [products]);

  const visible = useMemo(() => {
    let list = [...products];
    const needle = q.trim().toLowerCase();
    if (needle) list = list.filter((p) =>
      [p.name, p.sku, shortId(p.product_id), catName(p.category), p.size, p.finish]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle)));
    if (catFilter !== 'all') list = list.filter((p) => p.category === catFilter);
    if (stockFilter !== 'all') list = list.filter((p) => health(p).key === stockFilter);
    const by = {
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      category: (a, b) => (catName(a.category) || '').localeCompare(catName(b.category) || ''),
      price_asc: (a, b) => (a.price || 0) - (b.price || 0),
      price_desc: (a, b) => (b.price || 0) - (a.price || 0),
      stock_asc: (a, b) => (a.stock_quantity || 0) - (b.stock_quantity || 0),
      stock_desc: (a, b) => (b.stock_quantity || 0) - (a.stock_quantity || 0),
    };
    list.sort(by[sortBy] || by.name);
    return list;
  }, [products, q, catFilter, stockFilter, sortBy, categories]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/products`, {
        name: addForm.name, description: addForm.description, category: addForm.category,
        size: addForm.size, finish: addForm.finish || null, price: parseFloat(addForm.price),
        stock_quantity: parseInt(addForm.stock_quantity || '0', 10),
        sku: addForm.sku || null,
        low_stock_threshold: parseInt(addForm.low_stock_threshold || '15', 10),
      });
      toast.success('Product added');
      setShowAdd(false); setAddForm(emptyProduct); fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding product'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/api/products/${editForm.product_id}`, {
        name: editForm.name, description: editForm.description, category: editForm.category,
        size: editForm.size, finish: editForm.finish || null, price: parseFloat(editForm.price),
        sku: editForm.sku || '',
        low_stock_threshold: parseInt(editForm.low_stock_threshold ?? '15', 10),
      });
      toast.success('Product updated');
      setEditForm(null); fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating product'); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/products/${p.product_id}`);
      toast.success('Product deleted'); setDetailFor(null); fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete product'); }
  };

  return (
    <div className="space-y-6" data-testid="products-inventory">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Products &amp; Inventory</h2>
          <p className="text-slate-400 text-sm mt-1">Manage the TC product catalog, pricing and stock.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowActivity(true)} data-testid="inventory-activity-btn"
            className="inline-flex items-center gap-2 text-sm px-3.5 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-slate-200 hover:bg-white/[0.08] transition-colors">
            <History className="w-4 h-4" /> Activity
          </button>
          <button onClick={() => setShowCats(true)} data-testid="manage-categories-btn"
            className="inline-flex items-center gap-2 text-sm px-3.5 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-slate-200 hover:bg-white/[0.08] transition-colors">
            <Layers className="w-4 h-4" /> Categories
          </button>
          <button onClick={() => { setAddForm(emptyProduct); setShowAdd(true); }} data-testid="add-product-btn"
            className="inline-flex items-center gap-2 text-sm px-3.5 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium hover:bg-[#d4b36e] transition-colors">
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard icon={Package} accent="#C6A15B" label="Total Products" value={summary.total} testid="summary-total" />
        <button onClick={() => setStockFilter(stockFilter === 'low' ? 'all' : 'low')} data-testid="summary-low-btn" className="text-left">
          <SummaryCard icon={AlertTriangle} accent="#F59E0B" label="Low Stock" value={summary.low} active={stockFilter === 'low'} />
        </button>
        <button onClick={() => setStockFilter(stockFilter === 'out' ? 'all' : 'out')} data-testid="summary-out-btn" className="text-left">
          <SummaryCard icon={PackageX} accent="#EF4444" label="Out of Stock" value={summary.out} active={stockFilter === 'out'} />
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products, SKU, category..."
            className={`${inputCls} pl-9`} data-testid="product-search-input" />
        </div>
        <div className="flex flex-wrap gap-3">
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={`${inputCls} w-auto`} data-testid="category-filter">
            <option value="all">All Categories</option>
            {categories.map((c) => <option key={c.name} value={c.name}>{c.display_name}</option>)}
          </select>
          <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className={`${inputCls} w-auto`} data-testid="stock-status-filter">
            <option value="all">All Stock</option>
            <option value="in">In Stock</option>
            <option value="low">Low Stock</option>
            <option value="out">Out of Stock</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${inputCls} w-auto`} data-testid="sort-select">
            <option value="name">Name (A–Z)</option>
            <option value="category">Category</option>
            <option value="price_asc">Price (Low→High)</option>
            <option value="price_desc">Price (High→Low)</option>
            <option value="stock_asc">Stock (Lowest)</option>
            <option value="stock_desc">Stock (Highest)</option>
          </select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : visible.length === 0 ? (
        <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl py-16 text-center">
          <Boxes className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">No products match your filters</p>
          <p className="text-slate-500 text-sm mt-1">Try adjusting search or filters.</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-[#14171B] border border-white/[0.08] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-white/[0.06]">
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">SKU / ID</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium text-right">Base Price</th>
                  <th className="px-4 py-3 font-medium text-right">In Stock</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => (
                  <tr key={p.product_id} onClick={() => setDetailFor(p)} data-testid={`product-row-${p.product_id}`}
                    className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.03] cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-slate-100 font-medium">{p.name}</div>
                      <div className="text-slate-500 text-xs truncate max-w-[280px]">{p.size}{p.finish ? ` · ${p.finish}` : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{p.sku || shortId(p.product_id)}</td>
                    <td className="px-4 py-3 text-slate-300">{catName(p.category)}</td>
                    <td className="px-4 py-3 text-right text-slate-100">{money(p.price)}</td>
                    <td className="px-4 py-3 text-right text-slate-100 font-semibold">{p.stock_quantity ?? 0}</td>
                    <td className="px-4 py-3"><HealthBadge p={p} /></td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        <IconBtn title="Adjust inventory" onClick={() => setAdjustFor(p)} testid={`adjust-inventory-btn-${p.product_id}`}><Boxes className="w-4 h-4" /></IconBtn>
                        <IconBtn title="Edit" onClick={() => setEditForm({ ...p, price: p.price, low_stock_threshold: effThreshold(p) })} testid={`edit-product-btn-${p.product_id}`}><Pencil className="w-4 h-4" /></IconBtn>
                        <IconBtn title="Delete" danger onClick={() => handleDelete(p)} testid={`delete-product-btn-${p.product_id}`}><Trash2 className="w-4 h-4" /></IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {visible.map((p) => (
              <div key={p.product_id} onClick={() => setDetailFor(p)} data-testid={`product-card-${p.product_id}`}
                className="bg-[#14171B] border border-white/[0.08] rounded-xl p-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="text-slate-100 font-medium truncate">{p.name}</div>
                    <div className="text-slate-500 text-xs mt-0.5">{catName(p.category)} · {p.sku || shortId(p.product_id)}</div>
                  </div>
                  <HealthBadge p={p} />
                </div>
                <div className="flex items-center justify-between mt-3 text-sm">
                  <span className="text-slate-400">{money(p.price)}</span>
                  <span className="text-slate-100 font-semibold">{p.stock_quantity ?? 0} in stock</span>
                </div>
                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => setAdjustFor(p)} data-testid={`adjust-inventory-mbtn-${p.product_id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs py-2 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200"><Boxes className="w-3.5 h-3.5" /> Adjust</button>
                  <button onClick={() => setEditForm({ ...p, price: p.price, low_stock_threshold: effThreshold(p) })} data-testid={`edit-product-mbtn-${p.product_id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs py-2 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add / Edit dialogs */}
      <ProductFormModal open={showAdd} onClose={() => setShowAdd(false)} title="Add Product"
        form={addForm} setForm={setAddForm} onSubmit={handleAdd} categories={categories} withStock testid="add-product-dialog" />
      <ProductFormModal open={!!editForm} onClose={() => setEditForm(null)} title="Edit Product"
        form={editForm || {}} setForm={setEditForm} onSubmit={handleEdit} categories={categories} testid="edit-product-dialog" />

      {adjustFor && (
        <AdjustInventoryModal product={adjustFor} onClose={() => setAdjustFor(null)}
          onDone={() => { setAdjustFor(null); fetchProducts(); }} />
      )}

      {detailFor && (
        <ProductDetailDrawer product={products.find((p) => p.product_id === detailFor.product_id) || detailFor}
          catName={catName} onClose={() => setDetailFor(null)}
          onEdit={(p) => { setDetailFor(null); setEditForm({ ...p, price: p.price, low_stock_threshold: effThreshold(p) }); }}
          onAdjust={(p) => { setDetailFor(null); setAdjustFor(p); }}
          onDelete={handleDelete} />
      )}

      {showCats && <CategoryManager categories={categories} products={products} onClose={() => setShowCats(false)} onChanged={fetchCategories} />}
      {showActivity && <ActivityModal onClose={() => setShowActivity(false)} />}
    </div>
  );
};

const SummaryCard = ({ icon: Icon, accent, label, value, active, testid }) => (
  <div data-testid={testid} className={`bg-[#14171B] border rounded-2xl p-5 transition-colors ${active ? 'border-[#C6A15B]/60' : 'border-white/[0.08]'}`}>
    <div className="flex items-center justify-between">
      <span className="text-slate-400 text-sm">{label}</span>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}1f`, color: accent }}><Icon className="w-4 h-4" /></span>
    </div>
    <div className="text-2xl font-bold text-white mt-2">{value}</div>
  </div>
);

const IconBtn = ({ children, onClick, title, danger, testid }) => (
  <button title={title} data-testid={testid} onClick={onClick}
    className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border transition-colors ${danger ? 'border-red-500/20 text-red-400 hover:bg-red-500/10' : 'border-white/10 text-slate-300 hover:bg-white/[0.08]'}`}>
    {children}
  </button>
);

const ProductFormModal = ({ open, onClose, title, form, setForm, onSubmit, categories, withStock, testid }) => {
  const upd = (k, v) => setForm({ ...form, [k]: v });
  return (
    <Modal open={open} onClose={onClose} max="max-w-2xl" testid={testid}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
        <h3 className="text-white font-semibold">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={onSubmit} className="p-6 space-y-5">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Product Information</div>
        <div>
          <label className={labelCls}>Product Name</label>
          <input className={inputCls} value={form.name || ''} onChange={(e) => upd('name', e.target.value)} required data-testid="pf-name" />
        </div>
        <div>
          <label className={labelCls}>Description</label>
          <textarea className={inputCls} rows={3} value={form.description || ''} onChange={(e) => upd('description', e.target.value)} required data-testid="pf-description" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Category</label>
            <select className={inputCls} value={form.category || 'ppf'} onChange={(e) => upd('category', e.target.value)} data-testid="pf-category">
              {categories.map((c) => <option key={c.name} value={c.name}>{c.display_name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>SKU <span className="text-slate-600">(optional)</span></label>
            <input className={inputCls} value={form.sku || ''} onChange={(e) => upd('sku', e.target.value)} placeholder="e.g. TC-PPF-60-GLOSS" data-testid="pf-sku" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Size</label>
            <input className={inputCls} value={form.size || ''} onChange={(e) => upd('size', e.target.value)} placeholder={'e.g. 60"x50\''} required data-testid="pf-size" />
          </div>
          <div>
            <label className={labelCls}>Finish <span className="text-slate-600">(optional)</span></label>
            <input className={inputCls} value={form.finish || ''} onChange={(e) => upd('finish', e.target.value)} placeholder="matte, gloss, satin" data-testid="pf-finish" />
          </div>
        </div>

        <div className="text-[11px] uppercase tracking-wide text-slate-500 pt-1">Pricing &amp; Inventory</div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Base Price (USD)</label>
            <input type="number" step="0.01" className={inputCls} value={form.price ?? ''} onChange={(e) => upd('price', e.target.value)} placeholder="299.99" required data-testid="pf-price" />
          </div>
          {withStock ? (
            <div>
              <label className={labelCls}>Initial Stock</label>
              <input type="number" className={inputCls} value={form.stock_quantity ?? ''} onChange={(e) => upd('stock_quantity', e.target.value)} placeholder="100" required data-testid="pf-stock" />
            </div>
          ) : (
            <div className="flex items-end">
              <p className="text-xs text-slate-500 pb-2.5">Stock is managed via <span className="text-slate-300">Adjust Inventory</span>.</p>
            </div>
          )}
        </div>
        <div>
          <label className={labelCls}>Low Stock Alert — alert when inventory reaches</label>
          <div className="flex items-center gap-2">
            <input type="number" min="0" className={`${inputCls} w-28`} value={form.low_stock_threshold ?? '15'} onChange={(e) => upd('low_stock_threshold', e.target.value)} data-testid="pf-threshold" />
            <span className="text-sm text-slate-500">units</span>
          </div>
          <p className="text-xs text-slate-600 mt-1">Used for dashboard and inventory alerts.</p>
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm">Cancel</button>
          <button type="submit" className="flex-1 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium text-sm inline-flex items-center justify-center gap-2" data-testid="pf-submit"><Save className="w-4 h-4" /> Save</button>
        </div>
      </form>
    </Modal>
  );
};

const AdjustInventoryModal = ({ product, onClose, onDone }) => {
  const [mode, setMode] = useState('add');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState(ADJUST_REASONS[0]);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const current = product.stock_quantity ?? 0;
  const qn = parseInt(quantity || '0', 10) || 0;
  const preview = mode === 'add' ? current + qn : mode === 'remove' ? current - qn : qn;

  const submit = async () => {
    if (quantity === '' || qn < 0) { toast.error('Enter a valid quantity'); return; }
    if (mode === 'remove' && qn > current) { toast.error(`Only ${current} in stock`); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/products/${product.product_id}/adjust-inventory`, {
        mode, quantity: qn, reason, notes: notes || null,
      });
      toast.success('Inventory adjusted');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Adjustment failed'); }
    finally { setBusy(false); }
  };

  const ModeBtn = ({ id, label, icon: Icon }) => (
    <button type="button" onClick={() => setMode(id)} data-testid={`adjust-mode-${id}`}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm border transition-colors ${mode === id ? 'bg-[#C6A15B]/15 border-[#C6A15B]/60 text-[#C6A15B]' : 'bg-white/[0.04] border-white/10 text-slate-300'}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <Modal open onClose={onClose} testid="adjust-inventory-dialog">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
        <h3 className="text-white font-semibold flex items-center gap-2"><Boxes className="w-5 h-5 text-[#C6A15B]" /> Adjust Inventory</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6 space-y-5">
        <div>
          <div className="text-slate-300 text-sm">{product.name}</div>
          <div className="text-slate-500 text-xs">Current stock: <span className="text-slate-100 font-semibold" data-testid="adjust-current-stock">{current}</span></div>
        </div>
        <div className="flex gap-2">
          <ModeBtn id="add" label="Add" icon={ArrowUpRight} />
          <ModeBtn id="remove" label="Remove" icon={ArrowDownRight} />
          <ModeBtn id="set" label="Set Exact" icon={Minus} />
        </div>
        <div>
          <label className={labelCls}>Quantity</label>
          <input type="number" min="0" className={inputCls} value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" data-testid="adjust-quantity-input" />
        </div>
        <div>
          <label className={labelCls}>Reason</label>
          <select className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason-select">
            {ADJUST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Notes <span className="text-slate-600">(optional)</span></label>
          <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" data-testid="adjust-notes-input" />
        </div>
        <div className="flex items-center justify-between text-sm px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/10">
          <span className="text-slate-400">New stock level</span>
          <span className={`font-semibold ${preview < 0 ? 'text-red-400' : 'text-slate-100'}`} data-testid="adjust-preview">{preview}</span>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="flex-1 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="adjust-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirm Adjustment
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ProductDetailDrawer = ({ product, catName, onClose, onEdit, onAdjust, onDelete }) => {
  const [activity, setActivity] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/api/products/${product.product_id}/inventory-activity?limit=12`); setActivity(r.data.activity || []); }
      catch (e) { setActivity([]); }
    })();
  }, [product.product_id]);
  const m = health(product);
  return (
    <Modal open onClose={onClose} max="max-w-2xl" testid="product-detail-drawer">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
        <div>
          <h3 className="text-white font-semibold">{product.name}</h3>
          <p className="text-slate-500 text-xs font-mono mt-0.5">{product.sku || shortId(product.product_id)}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Field label="Category" value={catName(product.category)} />
          <Field label="Base Price" value={money(product.price)} />
          <Field label="Size" value={product.size || '—'} />
          <Field label="Finish" value={product.finish || '—'} />
        </div>
        {product.description && <p className="text-slate-400 text-sm">{product.description}</p>}

        <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-slate-400 text-xs">Current Stock</div>
              <div className="text-2xl font-bold text-white">{product.stock_quantity ?? 0}</div>
              <div className="text-slate-500 text-xs mt-0.5">Alert at {effThreshold(product)} units</div>
            </div>
            <div className="text-right space-y-2">
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: `${m.color}1f`, color: m.color }}>{m.label}</span>
              <div><button onClick={() => onAdjust(product)} data-testid="detail-adjust-btn" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-[#C6A15B] text-black font-medium"><Boxes className="w-3.5 h-3.5" /> Adjust Inventory</button></div>
            </div>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Recent Inventory Activity</div>
          {activity === null ? <Skeleton className="h-20" /> : activity.length === 0 ? (
            <p className="text-slate-500 text-sm">No inventory activity recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {activity.map((a) => <ActivityRow key={a.activity_id} a={a} />)}
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={() => onEdit(product)} data-testid="detail-edit-btn" className="flex-1 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 text-sm inline-flex items-center justify-center gap-2"><Pencil className="w-4 h-4" /> Edit Product</button>
          <button onClick={() => onDelete(product)} data-testid="detail-delete-btn" className="py-2.5 px-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm inline-flex items-center justify-center gap-2"><Trash2 className="w-4 h-4" /> Delete</button>
        </div>
      </div>
    </Modal>
  );
};

const Field = ({ label, value }) => (
  <div><div className="text-slate-500 text-xs">{label}</div><div className="text-slate-100 mt-0.5">{value}</div></div>
);

const ActivityRow = ({ a }) => {
  const positive = (a.quantity_change || 0) >= 0;
  return (
    <div className="flex items-center justify-between gap-3 text-sm bg-white/[0.02] rounded-lg px-3 py-2" data-testid="activity-row">
      <div className="min-w-0">
        <div className="text-slate-200 flex items-center gap-2">
          <span className={`inline-flex items-center gap-0.5 font-semibold ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
            {positive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            {positive ? '+' : ''}{a.quantity_change}
          </span>
          <span className="text-slate-300">{ACTIVITY_LABELS[a.activity_type] || a.activity_type}</span>
        </div>
        <div className="text-slate-500 text-xs">
          {a.quantity_before} → {a.quantity_after}
          {a.reason ? ` · ${a.reason}` : ''}{a.order_id ? ` · ${(a.order_number || a.order_id).toString().slice(0, 10)}` : ''}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-slate-500 text-xs">{fmtT(a.created_at)}</div>
        <div className="text-slate-600 text-[11px]">{a.changed_by_name}</div>
      </div>
    </div>
  );
};

const ActivityModal = ({ onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/api/inventory/activity?limit=60`); setRows(r.data.activity || []); }
      catch (e) { setRows([]); }
    })();
  }, []);
  return (
    <Modal open onClose={onClose} max="max-w-2xl" testid="inventory-activity-dialog">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
        <h3 className="text-white font-semibold flex items-center gap-2"><History className="w-5 h-5 text-[#C6A15B]" /> Inventory Activity</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6">
        {rows === null ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}</div>
          : rows.length === 0 ? <p className="text-slate-500 text-sm text-center py-8">No inventory activity yet.</p>
          : <div className="space-y-2">{rows.map((a) => (
              <div key={a.activity_id}>
                <div className="text-slate-400 text-xs mb-1">{a.product_name}</div>
                <ActivityRow a={a} />
              </div>
            ))}</div>}
      </div>
    </Modal>
  );
};

const CategoryManager = ({ categories, products, onClose, onChanged }) => {
  const [form, setForm] = useState({ name: '', display_name: '', description: '', icon: '' });
  const create = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/api/categories`, form); toast.success('Category created'); setForm({ name: '', display_name: '', description: '', icon: '' }); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error creating category'); }
  };
  const del = async (name, display) => {
    if (!window.confirm(`Delete category "${display}"?`)) return;
    try { await axios.delete(`${API}/api/categories/${name}`); toast.success('Category deleted'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error deleting category'); }
  };
  return (
    <Modal open onClose={onClose} max="max-w-2xl" testid="category-manager-dialog">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
        <h3 className="text-white font-semibold flex items-center gap-2"><Layers className="w-5 h-5 text-[#C6A15B]" /> Manage Categories</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6 space-y-6">
        <form onSubmit={create} className="space-y-4 border-b border-white/[0.08] pb-6">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={labelCls}>Category Code</label><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ceramic_coating" required data-testid="cat-name" /></div>
            <div><label className={labelCls}>Display Name</label><input className={inputCls} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Ceramic Coating" required data-testid="cat-display" /></div>
          </div>
          <div><label className={labelCls}>Description</label><textarea className={inputCls} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div><label className={labelCls}>Icon</label>
            <select className={inputCls} value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}>
              <option value="">Select icon...</option><option value="shield">Shield</option><option value="eye">Eye</option><option value="star">Star</option><option value="package">Package</option><option value="settings">Settings</option>
            </select>
          </div>
          <button type="submit" className="inline-flex items-center gap-2 text-sm px-3.5 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium" data-testid="cat-create-btn"><Plus className="w-4 h-4" /> Create Category</button>
        </form>
        <div className="space-y-2">
          {categories.map((c) => (
            <div key={c.name} className="flex items-center justify-between bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-[#C6A15B]/15 text-[#C6A15B] flex items-center justify-center"><Tag className="w-4 h-4" /></span>
                <div><div className="text-slate-100 text-sm font-medium">{c.display_name}</div><div className="text-slate-500 text-xs">{c.name}</div></div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">{products.filter((p) => p.category === c.name).length} products</span>
                {!['ppf', 'window_tint'].includes(c.name) && (
                  <button onClick={() => del(c.name, c.display_name)} className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default ProductsInventory;
